import React from 'react';
import { motion } from 'framer-motion';
import {
  LightBulbIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CurrencyDollarIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../../contexts/LanguageContext';

const QuickInsights: React.FC = () => {
  const { t } = useLanguage();

  const insights = [
    {
      type: 'recommendation',
      title: 'توصية ذكية للتوفير',
      message: 'يمكنك توفير 15,000 ر.س شهرياً عبر تحسين إنفاق قسم التسويق',
      action: 'عرض التفاصيل',
      icon: LightBulbIcon,
      color: 'from-yellow-500 to-orange-500',
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
      textColor: 'text-yellow-800 dark:text-yellow-200',
      priority: 'high'
    },
    {
      type: 'alert',
      title: 'تنبيه: تجاوز الميزانية',
      message: 'قسم المشتريات تجاوز الميزانية المحددة بنسبة 12% هذا الشهر',
      action: 'مراجعة الإنفاق',
      icon: ExclamationTriangleIcon,
      color: 'from-red-500 to-red-600',
      bgColor: 'bg-red-50 dark:bg-red-900/20',
      textColor: 'text-red-800 dark:text-red-200',
      priority: 'urgent'
    },
    {
      type: 'success',
      title: 'تحقيق هدف الوفورات',
      message: 'تم تحقيق 23,500 ر.س وفورات من الشراكات الذكية هذا الشهر',
      action: 'عرض الشراكات',
      icon: CheckCircleIcon,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      textColor: 'text-green-800 dark:text-green-200',
      priority: 'medium'
    },
    {
      type: 'info',
      title: 'فرصة تمويل فوري',
      message: 'متاح الآن تمويل بقيمة 500,000 ر.س بمعدل فائدة 3.5% سنوياً',
      action: 'طلب التمويل',
      icon: InformationCircleIcon,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      textColor: 'text-blue-800 dark:text-blue-200',
      priority: 'medium'
    }
  ];

  const trends = [
    {
      title: 'الإيرادات',
      value: '485,200 ر.س',
      change: '+8.2%',
      trend: 'up',
      icon: ArrowTrendingUpIcon
    },
    {
      title: 'المصروفات',
      value: '312,800 ر.س',
      change: '-3.1%',
      trend: 'down',
      icon: ArrowTrendingDownIcon
    },
    {
      title: 'الربح الصافي',
      value: '172,400 ر.س',
      change: '+15.3%',
      trend: 'up',
      icon: CurrencyDollarIcon
    },
    {
      title: 'معدل النمو',
      value: '12.5%',
      change: '+2.1%',
      trend: 'up',
      icon: ChartBarIcon
    }
  ];

  return (
    <div className="space-y-6">
      {/* AI Insights */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-100 dark:border-gray-700"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center">
            <LightBulbIcon className="w-6 h-6 text-yellow-500 ml-2 rtl:ml-0 rtl:mr-2" />
            {t('dashboard.ai_insights')}
          </h2>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            آخر تحديث: منذ 5 دقائق
          </span>
        </div>

        <div className="space-y-4">
          {insights.map((insight, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`${insight.bgColor} rounded-xl p-4 border ${
                insight.priority === 'urgent' 
                  ? 'border-red-200 dark:border-red-800' 
                  : insight.priority === 'high'
                  ? 'border-yellow-200 dark:border-yellow-800'
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className="flex items-start space-x-3 rtl:space-x-reverse">
                <div className={`w-10 h-10 bg-gradient-to-r ${insight.color} rounded-lg p-2 flex-shrink-0`}>
                  <insight.icon className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className={`text-sm font-semibold ${insight.textColor}`}>
                      {insight.title}
                    </h3>
                    {insight.priority === 'urgent' && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                        عاجل
                      </span>
                    )}
                  </div>
                  <p className={`text-sm ${insight.textColor} mt-1 opacity-90`}>
                    {insight.message}
                  </p>
                  <button className={`text-xs font-medium ${insight.textColor} hover:underline mt-2`}>
                    {insight.action} ←
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Quick Trends */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 border border-gray-100 dark:border-gray-700"
      >
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
          {t('dashboard.quick_trends')}
        </h2>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {trends.map((trend, index) => (
            <motion.div
              key={trend.title}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="text-center p-4 rounded-xl bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors duration-200"
            >
              <div className={`w-8 h-8 mx-auto mb-2 rounded-lg ${
                trend.trend === 'up' 
                  ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400' 
                  : 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400'
              } p-1.5`}>
                <trend.icon className="w-5 h-5" />
              </div>
              <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                {trend.title}
              </h3>
              <p className="text-sm font-bold text-gray-900 dark:text-white">
                {trend.value}
              </p>
              <p className={`text-xs font-medium ${
                trend.trend === 'up' 
                  ? 'text-green-600 dark:text-green-400' 
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {trend.change}
              </p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default QuickInsights;
